import { useMemo, useState } from 'react';
import {
  DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID,
  RICE_GROWTH_STAGE_COLORS,
  getRiceGrowthStagePeriod,
  type RiceGrowthStagePeriodId,
} from '../constants/riceGrowthStageLayers';
import { useTheme } from '../theme/ThemeContext';

function formatHectares(hectares: number): string {
  const fraction = Number.isInteger(hectares) ? 0 : 1;
  return `${hectares.toLocaleString('en-US', {
    minimumFractionDigits: fraction,
    maximumFractionDigits: 1,
  })} ha`;
}

function donutSlice(
  cx: number,
  cy: number,
  inner: number,
  outer: number,
  start: number,
  end: number,
): string {
  const large = end - start > Math.PI ? 1 : 0;
  const xo0 = cx + outer * Math.cos(start);
  const yo0 = cy + outer * Math.sin(start);
  const xo1 = cx + outer * Math.cos(end);
  const yo1 = cy + outer * Math.sin(end);
  const xi1 = cx + inner * Math.cos(end);
  const yi1 = cy + inner * Math.sin(end);
  const xi0 = cx + inner * Math.cos(start);
  const yi0 = cy + inner * Math.sin(start);
  return `M ${xo0} ${yo0} A ${outer} ${outer} 0 ${large} 1 ${xo1} ${yo1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
}

/** Nationwide rice growth-stage area. Hover or tap a slice to read hectares. */
export default function RiceGrowthStageAreaChart({
  periodId = DEFAULT_RICE_GROWTH_STAGE_PERIOD_ID,
}: {
  periodId?: RiceGrowthStagePeriodId;
}) {
  const { isDark } = useTheme();
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const period = getRiceGrowthStagePeriod(periodId);

  const slices = useMemo(() => {
    const drawn = period.area.filter((row) => row.hectares > 0);
    const total = drawn.reduce((sum, row) => sum + row.hectares, 0);
    let cursor = -Math.PI / 2;
    return drawn.map((row) => {
      const sweep = (row.hectares / total) * Math.PI * 2;
      const gap = sweep > 0.08 ? 0.012 : 0;
      const start = cursor + gap / 2;
      const end = cursor + sweep - gap / 2;
      cursor += sweep;
      return { ...row, start, end, color: RICE_GROWTH_STAGE_COLORS[row.stage] };
    });
  }, [period.area]);

  const active = period.area.find((row) => row.stage === activeStage) ?? null;
  const muted = isDark ? 'text-[#8b949e]' : 'text-[#6b7280]';
  const strong = isDark ? 'text-[#e6edf3]' : 'text-[#111827]';

  return (
    <div
      className={`rounded-lg border px-2.5 py-2 ${
        isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'
      }`}
      onPointerLeave={() => setActiveStage(null)}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className={`text-sm font-semibold ${strong}`}>Area by stage</p>
        <p className={`text-xs ${muted}`}>{period.label}</p>
      </div>

      <div className="flex items-center gap-3">
        <svg
          viewBox="0 0 120 120"
          className="h-[120px] w-[120px] shrink-0"
          role="img"
          aria-label="Rice growth stage area"
        >
          {slices.map((slice) => {
            const selected = activeStage === slice.stage;
            const dimmed = activeStage != null && !selected;
            return (
              <path
                key={slice.stage}
                d={donutSlice(60, 60, 34, 54, slice.start, slice.end)}
                fill={slice.color}
                opacity={dimmed ? 0.35 : 1}
                className="cursor-pointer outline-none"
                onPointerEnter={() => setActiveStage(slice.stage)}
                onClick={() => setActiveStage(slice.stage)}
              >
                <title>{`${slice.name}: ${formatHectares(slice.hectares)}`}</title>
              </path>
            );
          })}
        </svg>

        <div className="min-w-0 flex-1">
          {active ? (
            <>
              <p className={`text-sm font-semibold leading-snug ${strong}`}>{active.name}</p>
              <p className={`mt-1 text-lg font-semibold tabular-nums ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}>
                {formatHectares(active.hectares)}
              </p>
            </>
          ) : (
            <p className={`text-xs leading-snug ${muted}`}>
              Share of the country is listed below. Hover a stage to see its area.
            </p>
          )}
        </div>
      </div>

      <ul className="mt-3 space-y-1">
        {period.area.map((row) => {
          const selected = activeStage === row.stage;
          return (
            <li key={row.stage}>
              <button
                type="button"
                onPointerEnter={() => setActiveStage(row.stage)}
                onClick={() => setActiveStage(row.stage)}
                className={`flex w-full items-center gap-2 rounded px-1 py-1 text-left ${
                  selected ? (isDark ? 'bg-white/[0.06]' : 'bg-[#f3f4f6]') : ''
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: RICE_GROWTH_STAGE_COLORS[row.stage] }}
                />
                <span className={`min-w-0 flex-1 truncate text-xs ${selected ? strong : muted}`}>
                  {row.name}
                </span>
                <span className={`shrink-0 text-sm font-semibold tabular-nums ${selected ? (isDark ? 'text-[#34d399]' : 'text-[#008540]') : strong}`}>
                  {selected ? formatHectares(row.hectares) : `${row.share}%`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
